import { GlassOption } from '@cutr/constants/windows/windows-configurations.ts';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
} from 'react';

// Filter types based on the glass options structure
export type PanelType = GlassOption['layers'];
export type ExtraFilter =
  | 'antiBurglar'
  | 'temperedSafetyGlass'
  | 'ornaments'
  | 'sunProtection'
  | 'soundProtection'
  | 'laminatedSafetyGlass';

export interface FilterState {
  panelTypes: Set<PanelType>;
  extras: Set<ExtraFilter>;
}

interface FilterContextType {
  filters: FilterState;
  isExtraActive: (extra: ExtraFilter) => boolean;
  isPanelTypeActive: (type: PanelType) => boolean;
  toggleExtra: (extra: ExtraFilter) => void;
  togglePanelType: (type: PanelType) => void;
}

const FilterContext = createContext<FilterContextType | undefined>(undefined);

const initialState: FilterState = {
  panelTypes: new Set(),
  extras: new Set(),
};

export const FilterProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [filters, setFilters] = React.useState(initialState);

  const isPanelTypeActive = (type: PanelType): boolean => {
    return filters.panelTypes.has(type);
  };

  const isExtraActive = (extra: ExtraFilter): boolean => {
    return filters.extras.has(extra);
  };

  const toggleExtra = (extra: ExtraFilter) => {
    const newExtras = new Set(filters.extras);
    if (newExtras.has(extra)) {
      newExtras.delete(extra);
    } else {
      newExtras.add(extra);
    }

    setFilters({
      ...filters,
      extras: newExtras,
    });
  };

  const togglePanelType = (type: PanelType) => {
    const newPanelTypes = new Set(filters.panelTypes);

    if (newPanelTypes.has(type)) {
      newPanelTypes.delete(type);
    } else {
      newPanelTypes.add(type);
    }

    setFilters({
      ...filters,
      panelTypes: newPanelTypes,
    });
  };

  const contextValue: FilterContextType = {
    filters,
    isExtraActive,
    isPanelTypeActive,
    toggleExtra,
    togglePanelType,
  };

  return (
    <FilterContext.Provider value={contextValue}>
      {children}
    </FilterContext.Provider>
  );
};

export const useFilters = (): FilterContextType => {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error('useFilters must be used within a FilterProvider');
  }
  return context;
};

// Utility hook for filtering glass options
export const useGlassFilter = () => {
  const { filters } = useFilters();

  const filterGlassOptions = useCallback(
    (options: GlassOption[]) => {
      return options.filter((option) => {
        // Filter by panel types
        if (filters.panelTypes.size > 0) {
          if (!filters.panelTypes.has(option.layers)) {
            return false;
          }
        }

        // Filter by extras
        if (filters.extras.size > 0) {
          const hasMatchingExtra = Array.from(filters.extras).some((extra) => {
            return option[extra] === true;
          });

          if (!hasMatchingExtra) {
            return false;
          }
        }

        return true;
      });
    },
    [filters]
  );

  return { filterGlassOptions };
};
